import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { goalBarClass } from '@/utils/styleHelpers';
import { PageTabs } from '@/components/PageTabs';
import {
  Plus, ArrowLeft, Trash2, Save, Edit3, Target, Zap, Users, Award,
  Gauge, FolderKanban, Euro, Workflow, ChevronRight, X, TrendingUp,
  CheckCircle2, AlertTriangle, Clock, CircleDot, BarChart3
} from 'lucide-react';

const HUB_TABS = [
  { path: '/kpis', label: 'KPIs', icon: BarChart3 },
  { path: '/goals', label: 'Goals & OKRs', icon: Target },
];

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  process_management: Workflow, project_management: FolderKanban,
  process_control: Gauge, lean_six_sigma: Target,
  continuous_improvement: Zap, operational_excellence: Award,
  people_culture: Users, financial_impact: Euro, custom: CircleDot,
};
const CATEGORY_COLORS = {
  process_management: 'text-blue-400', project_management: 'text-violet-400',
  process_control: 'text-amber-400', lean_six_sigma: 'text-rose-400',
  continuous_improvement: 'text-emerald-400', operational_excellence: 'text-cyan-400',
  people_culture: 'text-pink-400', financial_impact: 'text-yellow-400', custom: 'text-gray-400',
};
const CATEGORY_BG = {
  process_management: 'bg-blue-500/10 border-blue-500/20', project_management: 'bg-violet-500/10 border-violet-500/20',
  process_control: 'bg-amber-500/10 border-amber-500/20', lean_six_sigma: 'bg-rose-500/10 border-rose-500/20',
  continuous_improvement: 'bg-emerald-500/10 border-emerald-500/20', operational_excellence: 'bg-cyan-500/10 border-cyan-500/20',
  people_culture: 'bg-pink-500/10 border-pink-500/20', financial_impact: 'bg-yellow-500/10 border-yellow-500/20', custom: 'bg-gray-500/10 border-gray-500/20',
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20', icon: Clock },
  on_track: { label: 'On Track', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', icon: TrendingUp },
  at_risk: { label: 'At Risk', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', icon: AlertTriangle },
  behind: { label: 'Behind', color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20', icon: AlertTriangle },
  completed: { label: 'Completed', color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20', icon: CheckCircle2 },
};

const STATUSES = ['not_started', 'on_track', 'at_risk', 'behind', 'completed'];
const LINKED_MODULES = [
  { value: 'none', label: 'None' }, { value: 'dmaic', label: 'DMAIC Projects' },
  { value: 'kaizen', label: 'Kaizen Events' }, { value: 'vsm', label: 'Value Stream' },
  { value: 'process_flow', label: 'Process Flow' }, { value: 'picking', label: 'Order Picking' },
  { value: 'expedition', label: 'Expedition' }, { value: '6s_audit', label: '6S Audits' },
  { value: 'kpi', label: 'KPIs' },
];

const calcProgress = (krs) => {
  if (!krs?.length) return 0;
  const valid = krs.filter(kr => kr.target > 0);
  if (!valid.length) return 0;
  return Math.round(valid.reduce((sum, kr) => sum + Math.min((kr.current / kr.target) * 100, 100), 0) / valid.length);
};

const ProgressBar = ({ value, className = '' }) => (
  <div className={`h-2 rounded-full bg-muted/50 overflow-hidden ${className}`}>
    <div className={`h-full rounded-full transition-all ${goalBarClass(value)}`} style={{ width: `${Math.min(value, 100)}%` }} />
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`} data-testid={`status-${status}`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </span>
  );
};

// ========================== SUMMARY CARDS ==========================

const SummaryCards = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Objectives', value: stats.total_objectives, icon: Target, color: 'text-blue-400' },
    { label: 'Key Results', value: stats.total_key_results, icon: BarChart3, color: 'text-emerald-400' },
    { label: 'KRs Completed', value: stats.completed_key_results, icon: CheckCircle2, color: 'text-cyan-400' },
    { label: 'Avg Progress', value: `${stats.avg_progress}%`, icon: TrendingUp, color: 'text-amber-400' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="goals-summary">
      {cards.map(c => (
        <div key={c.label} className="p-4 rounded-xl border border-muted/50 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <c.icon className={`h-4 w-4 ${c.color}`} />
            <span className="text-xs text-muted-foreground">{c.label}</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{c.value}</p>
        </div>
      ))}
    </div>
  );
};

// ========================== DETAIL / EDIT VIEW ==========================

const GoalDetail = ({ goalId, onBack, token, isAdmin }) => {
  const [goal, setGoal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/goals/${goalId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      setGoal(await r.json());
    } catch { toast.error('Failed to load goal'); }
  }, [goalId, token]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(goal))); setEditing(true); };
  const cancelEdit = () => { setDraft(null); setEditing(false); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/goals/${goalId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(draft),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      setGoal(await r.json()); setDraft(null); setEditing(false);
      toast.success('Goal saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const updateKR = (idx, field, value) => {
    const krs = [...draft.key_results];
    krs[idx] = { ...krs[idx], [field]: value };
    setDraft({ ...draft, key_results: krs });
  };
  const deleteKR = (idx) => setDraft({ ...draft, key_results: draft.key_results.filter((_, i) => i !== idx) });
  const addKR = () => setDraft({ ...draft, key_results: [...draft.key_results, { kr_id: `kr_${Date.now()}`, title: '', target: 100, current: 0, unit: '%', owner: '' }] });

  if (!goal) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const d = editing ? draft : goal;
  const progress = calcProgress(d.key_results);
  const CatIcon = CATEGORY_ICONS[d.category_key] || CircleDot;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="goal-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1 min-w-0">
          {editing ? <Input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} className="text-lg font-semibold h-10" data-testid="goal-title-input" />
            : <h2 className="text-xl font-semibold text-foreground" data-testid="goal-title">{d.title}</h2>}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-xs ${CATEGORY_COLORS[d.category_key] || 'text-gray-400'}`}><CatIcon className="h-3.5 w-3.5" />{d.category}</span>
            <span className="text-xs text-muted-foreground">{d.period}</span>
            {d.owner && <span className="text-xs text-muted-foreground"><Users className="inline h-3 w-3 mr-0.5" />{d.owner}</span>}
            <StatusBadge status={d.status} />
          </div>
        </div>
        {isAdmin && !editing && <Button onClick={startEdit} data-testid="goal-edit-btn"><Edit3 className="h-4 w-4 mr-2" />Edit</Button>}
        {editing && <>
          <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="goal-save-btn"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}</Button>
        </>}
      </div>

      {/* Progress overview */}
      <div className="p-4 rounded-xl border border-muted/50 bg-card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm font-bold text-foreground">{progress}%</span>
        </div>
        <ProgressBar value={progress} />
      </div>

      {editing && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-0.5 block">Period</label>
            <Input value={draft.period} onChange={e => setDraft({ ...draft, period: e.target.value })} placeholder="e.g. Q2 2026" className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-0.5 block">Start Date</label>
            <Input type="date" value={draft.period_start} onChange={e => setDraft({ ...draft, period_start: e.target.value })} className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-0.5 block">End Date</label>
            <Input type="date" value={draft.period_end} onChange={e => setDraft({ ...draft, period_end: e.target.value })} className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-0.5 block">Status</label>
            <select value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })} className="w-full h-8 text-sm px-2 rounded-md bg-background border border-muted" data-testid="goal-status-select">
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-0.5 block">Owner</label>
            <Input value={draft.owner} onChange={e => setDraft({ ...draft, owner: e.target.value })} placeholder="Person or team" className="h-8 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-0.5 block">Linked Module</label>
            <select value={draft.linked_module} onChange={e => setDraft({ ...draft, linked_module: e.target.value })} className="w-full h-8 text-sm px-2 rounded-md bg-background border border-muted">
              {LINKED_MODULES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground mb-0.5 block">Description</label>
            <textarea value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} rows={2} className="w-full px-2 py-1.5 text-sm rounded-md bg-background border border-muted focus:border-primary/50 resize-none" />
          </div>
        </div>
      )}

      {!editing && d.description && <p className="text-sm text-muted-foreground">{d.description}</p>}

      {/* Key Results */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Key Results ({d.key_results?.length || 0})</h3>
          {editing && <Button variant="outline" size="sm" onClick={addKR} data-testid="goal-add-kr"><Plus className="h-4 w-4 mr-1" />Add Key Result</Button>}
        </div>
        <div className="space-y-3">
          {(d.key_results || []).map((kr, i) => {
            const krProg = kr.target > 0 ? Math.round(Math.min((kr.current / kr.target) * 100, 100)) : 0;
            return editing ? (
              <div key={kr.kr_id} className="p-4 rounded-lg border border-muted/50 bg-muted/10 space-y-2" data-testid={`kr-editor-${kr.kr_id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">KR {i + 1}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteKR(i)}><X className="h-3.5 w-3.5 text-destructive" /></Button>
                </div>
                <Input value={kr.title} onChange={e => updateKR(i, 'title', e.target.value)} placeholder="Key result description" className="h-8 text-sm" />
                <div className="grid grid-cols-4 gap-2">
                  <div><label className="text-xs text-muted-foreground block">Target</label><Input type="number" value={kr.target} onChange={e => updateKR(i, 'target', parseFloat(e.target.value) || 0)} className="h-8 text-sm" /></div>
                  <div><label className="text-xs text-muted-foreground block">Current</label><Input type="number" value={kr.current} onChange={e => updateKR(i, 'current', parseFloat(e.target.value) || 0)} className="h-8 text-sm" /></div>
                  <div><label className="text-xs text-muted-foreground block">Unit</label><Input value={kr.unit} onChange={e => updateKR(i, 'unit', e.target.value)} placeholder="%, €, count" className="h-8 text-sm" /></div>
                  <div><label className="text-xs text-muted-foreground block">Owner</label><Input value={kr.owner} onChange={e => updateKR(i, 'owner', e.target.value)} placeholder="Person" className="h-8 text-sm" /></div>
                </div>
              </div>
            ) : (
              <div key={kr.kr_id} className="p-4 rounded-lg border border-muted/50 bg-card" data-testid={`kr-${kr.kr_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground flex-1">{kr.title}</p>
                  <span className="text-sm font-bold text-foreground ml-3">{kr.current} / {kr.target} {kr.unit}</span>
                </div>
                <ProgressBar value={krProg} />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-muted-foreground">{krProg}% complete</span>
                  {kr.owner && <span className="text-xs text-muted-foreground"><Users className="inline h-3 w-3 mr-0.5" />{kr.owner}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Metadata */}
      {!editing && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t border-muted/50">
          <span>Created by {d.created_by_name}</span>
          <span>Created {new Date(d.created_at).toLocaleDateString()}</span>
          {d.linked_module && d.linked_module !== 'none' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary">
              Linked: {LINKED_MODULES.find(m => m.value === d.linked_module)?.label || d.linked_module}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ========================== LIST VIEW ==========================

const GoalsList = ({ onSelect, token, isAdmin }) => {
  const [goals, setGoals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set('category', filterCat);
      if (filterStatus) params.set('status', filterStatus);
      const [gr, tr, sr] = await Promise.all([
        fetch(`${API}/api/goals?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/goals/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/goals/summary/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (gr.ok) setGoals(await gr.json());
      if (tr.ok) setTemplates(await tr.json());
      if (sr.ok) setStats(await sr.json());
    } catch { toast.error('Failed to load goals'); }
    setLoading(false);
  }, [token, filterCat, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const createFromTemplate = async (templateKey) => {
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/goals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template_key: templateKey }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Create failed'); }
      const data = await r.json();
      toast.success(`Created ${data.created} OKR(s)`);
      setShowTemplates(false); load();
    } catch (e) { toast.error(e.message); } finally { setCreating(false); }
  };

  const createCustom = async () => {
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/goals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: 'New Objective', category: 'Custom', category_key: 'custom', key_results: [{ kr_id: `kr_${Date.now()}`, title: 'Key Result 1', target: 100, current: 0, unit: '%', owner: '' }] }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      toast.success('Custom objective created');
      setShowTemplates(false);
      onSelect(data.goal_id);
    } catch { toast.error('Create failed'); } finally { setCreating(false); }
  };

  const deleteGoal = async (goalId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this objective?')) return;
    try {
      await fetch(`${API}/api/goals/${goalId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      toast.success('Objective deleted'); load();
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const categories = [...new Set(goals.map(g => g.category))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Goals & OKRs</h2>
          <p className="text-sm text-muted-foreground mt-1">Strategic objectives and measurable key results</p>
        </div>
        {isAdmin && <Button onClick={() => setShowTemplates(!showTemplates)} disabled={creating} data-testid="goals-new-btn"><Plus className="h-4 w-4 mr-2" />New Objective</Button>}
      </div>

      <SummaryCards stats={stats} />

      {showTemplates && (
        <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-3" data-testid="goals-template-picker">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Choose a category template or create custom:</p>
            <Button variant="outline" size="sm" onClick={createCustom} disabled={creating} data-testid="goals-custom-btn"><Plus className="h-3 w-3 mr-1" />Custom Objective</Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {templates.map(t => {
              const Icon = CATEGORY_ICONS[t.key] || CircleDot;
              return (
                <button key={t.key} onClick={() => createFromTemplate(t.key)} disabled={creating}
                  className={`p-3 rounded-lg border text-left transition-all hover:shadow-md cursor-pointer ${CATEGORY_BG[t.key]}`}
                  data-testid={`goals-template-${t.key}`}
                >
                  <Icon className={`h-5 w-5 mb-1.5 ${CATEGORY_COLORS[t.key]}`} />
                  <p className="text-xs font-semibold text-foreground">{t.category}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="h-8 text-sm px-2 rounded-md bg-background border border-muted" data-testid="goals-filter-category">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-8 text-sm px-2 rounded-md bg-background border border-muted" data-testid="goals-filter-status">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
        </select>
        {(filterCat || filterStatus) && <Button variant="ghost" size="sm" onClick={() => { setFilterCat(''); setFilterStatus(''); }}>Clear</Button>}
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Target className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No objectives yet</p>
          <p className="text-sm mt-1">Create objectives from templates or build your own</p>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map(g => {
            const progress = calcProgress(g.key_results);
            const CatIcon = CATEGORY_ICONS[g.category_key] || CircleDot;
            return (
              <div key={g.goal_id} onClick={() => onSelect(g.goal_id)}
                className="p-4 rounded-xl border border-muted/50 bg-card hover:border-primary/30 transition-all cursor-pointer group"
                data-testid={`goal-item-${g.goal_id}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${CATEGORY_BG[g.category_key] || CATEGORY_BG.custom}`}>
                    <CatIcon className={`h-4 w-4 ${CATEGORY_COLORS[g.category_key] || 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{g.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">{g.category}</span>
                      <span className="text-xs text-muted-foreground">{g.period}</span>
                      {g.owner && <span className="text-xs text-muted-foreground">{g.owner}</span>}
                    </div>
                  </div>
                  <StatusBadge status={g.status} />
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={(e) => deleteGoal(g.goal_id, e)} data-testid={`goal-delete-${g.goal_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex items-center gap-3">
                  <ProgressBar value={progress} className="flex-1" />
                  <span className="text-xs font-medium text-foreground w-10 text-right">{progress}%</span>
                  <span className="text-xs text-muted-foreground">{g.key_results?.length || 0} KRs</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========================== MAIN PAGE ==========================

export const GoalsPage = () => {
  const { user } = useAuth();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin' || user?.is_master_admin;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="goals-page">
        {!selectedGoal && <PageTabs tabs={HUB_TABS} testIdPrefix="goals-tab" />}
        {selectedGoal ? (
          <GoalDetail goalId={selectedGoal} onBack={() => setSelectedGoal(null)} token={token} isAdmin={isAdmin} />
        ) : (
          <GoalsList onSelect={setSelectedGoal} token={token} isAdmin={isAdmin} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default GoalsPage;
